import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate, Link } from 'react-router-dom';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Loader2, Eye, EyeOff, Mail, Lock, User, Phone, MessageCircle, KeyRound, ArrowLeft, CheckCircle2 } from 'lucide-react';

const SUPPORT_WHATSAPP = '5581981739909';
const whatsappResetUrl = (email) => {
  const txt = email
    ? `Olá, sou usuário do Tô de Olho. Não consegui receber o código de recuperação. Meu e-mail cadastrado é: ${email}. Pode me ajudar?`
    : 'Olá, sou usuário do Tô de Olho. Preciso de ajuda para recuperar minha senha.';
  return `https://wa.me/${SUPPORT_WHATSAPP}?text=${encodeURIComponent(txt)}`;
};

// Inline SVG of the Google "G" so we don't depend on extra icon packages.
const GoogleIcon = ({ className = 'w-4 h-4' }) => (
  <svg className={className} viewBox="0 0 48 48" aria-hidden="true">
    <path fill="#FFC107" d="M43.611 20.083H42V20H24v8h11.303c-1.649 4.657-6.08 8-11.303 8-6.627 0-12-5.373-12-12s5.373-12 12-12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 12.955 4 4 12.955 4 24s8.955 20 20 20 20-8.955 20-20c0-1.341-.138-2.65-.389-3.917z" />
    <path fill="#FF3D00" d="m6.306 14.691 6.571 4.819C14.655 15.108 18.961 12 24 12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 16.318 4 9.656 8.337 6.306 14.691z" />
    <path fill="#4CAF50" d="M24 44c5.166 0 9.86-1.977 13.409-5.192l-6.19-5.238C29.211 34.91 26.715 36 24 36c-5.202 0-9.619-3.317-11.282-7.946l-6.522 5.025C9.505 39.556 16.227 44 24 44z" />
    <path fill="#1976D2" d="M43.611 20.083H42V20H24v8h11.303a12.04 12.04 0 0 1-4.087 5.571l.003-.002 6.19 5.238C36.971 39.205 44 34 44 24c0-1.341-.138-2.65-.389-3.917z" />
  </svg>
);

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
const startGoogleLogin = () => {
  const redirectUrl = window.location.origin + '/auth/callback';
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
};

const formatError = (detail) => {
  if (!detail) return 'Erro ao processar. Tente novamente.';
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    const msgs = detail.map(e => {
      const field = e?.loc?.slice(-1)?.[0];
      const msg = e?.msg || '';
      if (field === 'nome') return 'Nome é obrigatório.';
      if (field === 'email') return 'E-mail é obrigatório.';
      if (field === 'senha') return 'Senha é obrigatória.';
      return msg;
    }).filter(Boolean);
    return msgs.join(' ') || 'Verifique os campos e tente novamente.';
  }
  return String(detail);
};

const AuthPage = () => {
  const [mode, setMode] = useState('login');
  const [loginBy, setLoginBy] = useState('email'); // 'email' or 'phone'
  const [email, setEmail] = useState('');
  const [senha, setSenha] = useState('');
  const [nome, setNome] = useState('');
  const [telefone, setTelefone] = useState('');
  const [emailRecuperacao, setEmailRecuperacao] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [resetEmail, setResetEmail] = useState('');
  const [resetUsarRec, setResetUsarRec] = useState(false);
  const [resetSent, setResetSent] = useState(false);
  // Step-2 (consume 6-digit code) state
  const [resetCodigo, setResetCodigo] = useState('');
  const [resetNovaSenha, setResetNovaSenha] = useState('');
  const [resetConfirmSenha, setResetConfirmSenha] = useState('');
  const [resetShowPwd, setResetShowPwd] = useState(false);
  const [resetSuccess, setResetSuccess] = useState(false);
  const { login: loginFn, register } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Frontend validation
    if (mode === 'register') {
      if (!nome.trim()) { setError('Informe seu nome.'); return; }
      if (nome.trim().length < 2) { setError('Nome deve ter pelo menos 2 caracteres.'); return; }
      if (!email.trim()) { setError('Informe seu e-mail.'); return; }
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) { setError('E-mail inválido.'); return; }
    } else {
      if (loginBy === 'email') {
        if (!email.trim()) { setError('Informe seu e-mail.'); return; }
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) { setError('E-mail inválido.'); return; }
      } else {
        if (!telefone.replace(/\D/g, '')) { setError('Informe seu telefone.'); return; }
      }
    }
    if (!senha) { setError('Informe sua senha.'); return; }
    if (senha.length < 6) { setError('Senha deve ter pelo menos 6 caracteres.'); return; }

    setLoading(true);
    try {
      if (mode === 'login') {
        if (loginBy === 'phone') {
          const rawPhone = telefone.replace(/\D/g, '');
          if (rawPhone.length < 10) { setError('Telefone inválido.'); setLoading(false); return; }
          await loginFn(null, rawPhone, senha);
        } else {
          await loginFn(email.trim(), null, senha);
        }
      } else {
        await register(
          nome.trim(),
          email.trim(),
          telefone.replace(/\D/g, ''),
          senha,
          emailRecuperacao.trim().toLowerCase() || null,
        );
      }
      navigate('/');
    } catch (err) {
      // Log real error to console for diagnostics (users/devs via DevTools)
      // eslint-disable-next-line no-console
      console.error('[Auth] request failed:', {
        status: err?.response?.status,
        statusText: err?.response?.statusText,
        detail: err?.response?.data?.detail,
        message: err?.message,
        code: err?.code,
      });

      const detail = err.response?.data?.detail;
      const status = err.response?.status;

      if (status === 429) {
        setError('Muitas tentativas em sequência. Aguarde alguns instantes e tente novamente.');
      } else if (status === 401 || status === 403) {
        setError(typeof detail === 'string' ? detail : 'Credenciais inválidas. Verifique e-mail e senha.');
      } else if (status === 409) {
        setError('E-mail já cadastrado. Faça login ou recupere sua senha.');
      } else if (detail) {
        setError(formatError(detail));
      } else if (err.response) {
        if (status >= 500) {
          setError('O servidor está com instabilidade. Tente novamente em instantes.');
        } else {
          setError(err.response.statusText || `Erro ${status || ''}. Tente novamente.`);
        }
      } else if (err.code === 'ECONNABORTED' || /timeout/i.test(err.message || '')) {
        setError('O servidor demorou para responder. Tente novamente.');
      } else if (err.message === 'Network Error') {
        // CORS, DNS, or offline
        setError('Não foi possível conectar ao servidor. Verifique sua conexão e tente novamente.');
      } else {
        setError('Erro ao processar. Tente novamente.');
      }
    } finally { setLoading(false); }
  };

  const handleForgotPassword = async () => {
    if (!resetEmail.trim()) { setError('Informe seu e-mail.'); return; }
    setLoading(true);
    try {
      const API = process.env.REACT_APP_BACKEND_URL;
      await fetch(`${API}/api/auth/forgot-password`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: resetEmail.trim().toLowerCase(), usar_recuperacao: resetUsarRec }),
      });
      setResetSent(true);
      setError('');
    } catch { setError('Erro ao enviar. Tente novamente.'); }
    finally { setLoading(false); }
  };

  const handleConsumeResetCode = async (e) => {
    e?.preventDefault();
    setError('');
    if (!/^\d{6}$/.test(resetCodigo.trim())) { setError('O código deve ter 6 dígitos.'); return; }
    if (resetNovaSenha.length < 8) { setError('A nova senha deve ter ao menos 8 caracteres.'); return; }
    if (resetNovaSenha !== resetConfirmSenha) { setError('As senhas não coincidem.'); return; }
    setLoading(true);
    try {
      const API = process.env.REACT_APP_BACKEND_URL;
      const resp = await fetch(`${API}/api/auth/reset-password`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: resetEmail.trim().toLowerCase(),
          codigo: resetCodigo.trim(),
          nova_senha: resetNovaSenha,
        }),
      });
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok) {
        setError(typeof data?.detail === 'string' ? data.detail : 'Código inválido ou expirado.');
        return;
      }
      setResetSuccess(true);
    } catch { setError('Erro ao redefinir. Tente novamente.'); }
    finally { setLoading(false); }
  };

  const resetForgotState = () => {
    setMode('login');
    setResetSent(false); setResetSuccess(false);
    setResetCodigo(''); setResetNovaSenha(''); setResetConfirmSenha('');
    setError('');
  };

  if (mode === 'forgot') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F4F6F8] px-4 py-8" data-testid="forgot-password-page">
        <Card className="w-full max-w-md p-6 bg-white border border-[#E2E8F0] rounded-md">
          <div className="text-center mb-5">
            <img src="/logo.png" alt="Tô de Olho" className="h-14 mx-auto mb-3 rounded-lg" />
            <h2 className="text-xl font-black text-[#0F1C2E]">Recuperar Senha</h2>
            <p className="text-[11px] text-[#94A3B8] mt-1">Você receberá um <strong>código de 6 dígitos</strong> (válido por 15 min).</p>
          </div>

          {resetSuccess ? (
            <div className="text-center py-4" data-testid="forgot-success">
              <CheckCircle2 className="w-12 h-12 text-[#006030] mx-auto mb-3" />
              <h3 className="text-base font-black text-[#0F1C2E] mb-1">Senha redefinida!</h3>
              <p className="text-sm text-[#475569] mb-4">Sua nova senha já está ativa. Faça login.</p>
              <Button onClick={resetForgotState}
                      className="w-full bg-[#006030] hover:bg-[#004020] text-white"
                      data-testid="forgot-back-login">
                Ir para o login
              </Button>
            </div>
          ) : !resetSent ? (
            <>
              {error && <p className="text-xs text-[#C62828] mb-3 bg-[#FFEBEE] p-2 rounded" data-testid="error-msg">{error}</p>}
              <div className="space-y-3">
                <div>
                  <Label className="text-xs text-[#475569]">E-mail</Label>
                  <Input value={resetEmail} onChange={e => setResetEmail(e.target.value)} type="email" placeholder="seu@email.com" className="mt-1" data-testid="forgot-email" />
                </div>
                <label className="flex items-start gap-2 text-[11px] text-[#475569]">
                  <input type="checkbox" checked={resetUsarRec}
                         onChange={(e) => setResetUsarRec(e.target.checked)}
                         className="mt-0.5" data-testid="forgot-usar-rec" />
                  <span>Enviar para meu <strong>e-mail de recuperação</strong> (se cadastrado e verificado).</span>
                </label>
                <Button onClick={handleForgotPassword} disabled={loading}
                        className="w-full bg-[#006030] hover:bg-[#004020] text-white"
                        data-testid="forgot-submit">
                  {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Enviar código'}
                </Button>
              </div>
            </>
          ) : (
            <>
              <div className="rounded border border-[#BBF7D0] bg-[#F0FDF4] p-2.5 mb-3 text-center">
                <p className="text-[11px] text-[#006030]">
                  Se o e-mail estiver cadastrado, enviamos um código de 6 dígitos.<br />
                  <span className="text-[#475569]">Não chegou? Verifique a caixa de spam ou use o WhatsApp abaixo.</span>
                </p>
              </div>
              {error && <p className="text-xs text-[#C62828] mb-3 bg-[#FFEBEE] p-2 rounded" data-testid="error-msg">{error}</p>}
              <form onSubmit={handleConsumeResetCode} className="space-y-3">
                <div>
                  <Label className="text-xs text-[#475569]">Código recebido (6 dígitos)</Label>
                  <Input
                    value={resetCodigo}
                    onChange={(e) => setResetCodigo(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    placeholder="123456"
                    inputMode="numeric"
                    maxLength={6}
                    className="mt-1 text-center text-lg tracking-[0.5em] font-mono font-black"
                    data-testid="forgot-codigo"
                  />
                </div>
                <div>
                  <Label className="text-xs text-[#475569]">Nova senha</Label>
                  <div className="relative mt-1">
                    <Lock className="absolute left-3 top-2.5 w-4 h-4 text-[#94A3B8]" />
                    <Input
                      type={resetShowPwd ? 'text' : 'password'}
                      value={resetNovaSenha}
                      onChange={(e) => setResetNovaSenha(e.target.value)}
                      placeholder="Mínimo 8 caracteres"
                      className="pl-9 pr-10"
                      autoComplete="new-password"
                      data-testid="forgot-nova-senha"
                    />
                    <button type="button" onClick={() => setResetShowPwd(s => !s)}
                            className="absolute right-3 top-2.5 text-[#94A3B8] hover:text-[#475569]"
                            aria-label={resetShowPwd ? 'Ocultar' : 'Mostrar'}>
                      {resetShowPwd ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
                <div>
                  <Label className="text-xs text-[#475569]">Confirmar nova senha</Label>
                  <Input
                    type={resetShowPwd ? 'text' : 'password'}
                    value={resetConfirmSenha}
                    onChange={(e) => setResetConfirmSenha(e.target.value)}
                    placeholder="Repita a senha"
                    className="mt-1"
                    autoComplete="new-password"
                    data-testid="forgot-confirm-senha"
                  />
                </div>
                <Button type="submit" disabled={loading}
                        className="w-full bg-[#006030] hover:bg-[#004020] text-white"
                        data-testid="forgot-reset-submit">
                  {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : (<><KeyRound className="w-4 h-4 mr-2" /> Redefinir senha</>)}
                </Button>
              </form>
              <p className="text-[11px] text-[#94A3B8] text-center mt-3">
                <button onClick={() => { setResetSent(false); setError(''); }}
                        className="hover:text-[#006030] underline"
                        data-testid="forgot-resend">
                  Não recebi. Reenviar código
                </button>
              </p>
            </>
          )}

          {/* WhatsApp fallback — sempre visível enquanto não terminou */}
          {!resetSuccess && (
            <>
              <div className="flex items-center gap-2 my-4">
                <div className="flex-1 h-px bg-[#E2E8F0]" />
                <span className="text-[10px] text-[#94A3B8] uppercase tracking-wide">ou</span>
                <div className="flex-1 h-px bg-[#E2E8F0]" />
              </div>
              <a
                href={whatsappResetUrl(resetEmail)}
                target="_blank" rel="noopener noreferrer"
                className="w-full inline-flex items-center justify-center gap-2 bg-[#25D366] hover:bg-[#1ebe5d] text-white font-bold px-3 py-2.5 rounded text-sm"
                data-testid="forgot-whatsapp"
              >
                <MessageCircle className="w-4 h-4" /> Recuperar via WhatsApp
              </a>
              <p className="text-[10px] text-[#94A3B8] text-center mt-1.5">
                Suporte humano em horário comercial.
              </p>
            </>
          )}

          <button
            onClick={resetForgotState}
            className="block w-full mt-4 text-xs text-[#475569] hover:text-[#006030] inline-flex items-center justify-center gap-1"
            data-testid="forgot-back"
          >
            <ArrowLeft className="w-3 h-3" /> Voltar ao login
          </button>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#F4F6F8] px-4" data-testid="auth-page">
      <Card className="w-full max-w-md p-6 bg-white border border-[#E2E8F0] rounded-md">
        <div className="text-center mb-6">
          <img src="/logo.png" alt="Tô de Olho" className="h-14 mx-auto mb-3 rounded-lg" />
          <h2 className="text-xl font-black text-[#0F1C2E]">{mode === 'login' ? 'Entrar' : 'Criar Conta'}</h2>
          <p className="text-xs text-[#94A3B8] mt-1">Plataforma de análise de candidatos</p>
        </div>
        {error && <p className="text-xs text-[#C62828] mb-3 bg-[#FFEBEE] p-2 rounded" data-testid="error-msg">{error}</p>}

        {/* Google OAuth — primary social login (Emergent-managed) */}
        <Button
          type="button"
          onClick={startGoogleLogin}
          variant="outline"
          className="w-full h-11 border border-[#E2E8F0] text-[#0F1C2E] font-medium flex items-center justify-center gap-2 hover:bg-[#F4F6F8]"
          data-testid="google-login-btn"
        >
          <GoogleIcon className="w-4 h-4" />
          {mode === 'login' ? 'Entrar com Google' : 'Continuar com Google'}
        </Button>

        <div className="flex items-center gap-2 my-3" aria-hidden="true">
          <span className="flex-1 h-px bg-[#E2E8F0]" />
          <span className="text-[10px] text-[#94A3B8] uppercase tracking-wider">ou</span>
          <span className="flex-1 h-px bg-[#E2E8F0]" />
        </div>

        <form onSubmit={handleSubmit} className="space-y-3">
          {mode === 'register' && (
            <>
              <div>
                <Label className="text-xs text-[#475569]">Nome</Label>
                <div className="relative mt-1"><User className="absolute left-3 top-2.5 w-4 h-4 text-[#94A3B8]" /><Input value={nome} onChange={e => setNome(e.target.value)} placeholder="Seu nome" className="pl-9" required data-testid="register-nome" /></div>
              </div>
            </>
          )}
          {mode === 'login' && (
            <div className="flex rounded-md border border-[#E2E8F0] overflow-hidden mb-1">
              <button type="button" onClick={() => setLoginBy('email')} className={`flex-1 py-2 text-xs font-medium flex items-center justify-center gap-1 transition-colors ${loginBy === 'email' ? 'bg-[#006030] text-white' : 'bg-white text-[#475569] hover:bg-[#F4F6F8]'}`} data-testid="login-by-email-tab">
                <Mail className="w-3 h-3" /> E-mail
              </button>
              <button type="button" onClick={() => setLoginBy('phone')} className={`flex-1 py-2 text-xs font-medium flex items-center justify-center gap-1 transition-colors ${loginBy === 'phone' ? 'bg-[#006030] text-white' : 'bg-white text-[#475569] hover:bg-[#F4F6F8]'}`} data-testid="login-by-phone-tab">
                <Phone className="w-3 h-3" /> Telefone
              </button>
            </div>
          )}
          {(mode === 'register' || loginBy === 'email') && (
            <div>
              <Label className="text-xs text-[#475569]">E-mail</Label>
              <div className="relative mt-1"><Mail className="absolute left-3 top-2.5 w-4 h-4 text-[#94A3B8]" /><Input value={email} onChange={e => setEmail(e.target.value)} type="email" placeholder="seu@email.com" className="pl-9" required={mode === 'register' || loginBy === 'email'} data-testid="auth-email" /></div>
            </div>
          )}
          {mode === 'register' && (
            <div>
              <Label className="text-xs text-[#475569]">Telefone <span className="text-[#94A3B8]">(opcional)</span></Label>
              <div className="relative mt-1"><Phone className="absolute left-3 top-2.5 w-4 h-4 text-[#94A3B8]" /><Input value={telefone} onChange={e => setTelefone(e.target.value)} placeholder="(11) 99999-9999" className="pl-9" data-testid="register-telefone" /></div>
            </div>
          )}
          {mode === 'register' && (
            <div>
              <Label className="text-xs text-[#475569]">
                E-mail de recuperação <span className="text-[#94A3B8]">(recomendado)</span>
              </Label>
              <div className="relative mt-1">
                <Mail className="absolute left-3 top-2.5 w-4 h-4 text-[#94A3B8]" />
                <Input
                  value={emailRecuperacao}
                  onChange={e => setEmailRecuperacao(e.target.value)}
                  type="email"
                  placeholder="outro@email.com"
                  className="pl-9"
                  data-testid="register-email-recuperacao"
                />
              </div>
              <p className="text-[10px] text-[#94A3B8] mt-1">Usado para recuperar acesso caso perca o e-mail principal.</p>
            </div>
          )}
          {mode === 'login' && loginBy === 'phone' && (
            <div>
              <Label className="text-xs text-[#475569]">Telefone</Label>
              <div className="relative mt-1"><Phone className="absolute left-3 top-2.5 w-4 h-4 text-[#94A3B8]" /><Input value={telefone} onChange={e => setTelefone(e.target.value)} placeholder="(11) 99999-9999" className="pl-9" required data-testid="auth-telefone" /></div>
            </div>
          )}
          <div>
            <Label className="text-xs text-[#475569]">Senha</Label>
            <div className="relative mt-1">
              <Lock className="absolute left-3 top-2.5 w-4 h-4 text-[#94A3B8]" />
              <Input value={senha} onChange={e => setSenha(e.target.value)} type={showPassword ? 'text' : 'password'} placeholder="Sua senha" className="pl-9 pr-9" required data-testid="auth-senha" />
              <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-2.5 text-[#94A3B8]">{showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}</button>
            </div>
          </div>
          {mode === 'login' && (
            <p className="text-[10px] text-[#006030] text-right cursor-pointer hover:underline" onClick={() => setMode('forgot')} data-testid="forgot-link">Esqueceu a senha?</p>
          )}
          <Button type="submit" disabled={loading} className="w-full bg-[#006030] hover:bg-[#004020] text-white font-medium h-10" data-testid="auth-submit">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : mode === 'login' ? 'Entrar' : 'Criar Conta'}
          </Button>
        </form>
        <div className="mt-4 text-center">
          <p className="text-xs text-[#94A3B8]">
            {mode === 'login' ? 'Não tem conta?' : 'Já tem conta?'}
            <span className="text-[#006030] font-medium cursor-pointer ml-1 hover:underline" onClick={() => { setMode(mode === 'login' ? 'register' : 'login'); setError(''); }} data-testid="toggle-mode">
              {mode === 'login' ? 'Criar conta' : 'Entrar'}
            </span>
          </p>
        </div>
        <p className="text-[10px] text-[#94A3B8] text-center mt-3">
          Ao continuar, você concorda com os <Link to="/termos" className="text-[#006030] hover:underline">Termos de Uso</Link> e a <Link to="/privacidade" className="text-[#006030] hover:underline">Política de Privacidade</Link>.
        </p>
      </Card>
    </div>
  );
};

export default AuthPage;
